import React, { useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { isValidNumber, getInternationalPhoneNumber } from '../../../../pure-js/libs/PhoneHelper'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import useAppState from '../../hooks/useAppState'
import GrooverText from '../GrooverText'
import { PhoneInputWithFlag } from '../PhoneInputWithFlag'

interface EmailLoginFormProps {
  initialPhoneNumber?: string
  onSubmit: (phoneNumber: string, email: string) => Promise<void>
  errorMessage?: string
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 400px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #000000;
  margin: 0;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
  text-align: center;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333333;
`

const EmailInput = styled.input`
  padding: 12px 16px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--portal-primary, #7c3aed);
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`

const SubmitButton = styled.button`
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--portal-primary-dark, #6d28d9);
  }

  &:disabled {
    background: #4a4a4a;
    cursor: not-allowed;
  }
`

const ErrorText = styled.p`
  font-size: 14px;
  color: #ef4444;
  margin: 0;
`

const DEFAULT_PHONE_COUNTRY = 'SE'

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

export const EmailLoginForm: React.FC<EmailLoginFormProps> = ({ initialPhoneNumber, onSubmit, errorMessage }) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber ?? '')
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState<string>(DEFAULT_PHONE_COUNTRY)
  const [email, setEmail] = useState('')
  const [localError, setLocalError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage

  const isPhoneValid = useMemo(() => {
    if (!phoneNumber.trim()) return false
    return isValidNumber(phoneNumber, selectedPhoneCountry as any)
  }, [phoneNumber, selectedPhoneCountry])

  const isEmailValid = useMemo(() => isValidEmail(email), [email])

  const displayError = errorMessage ?? localError

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isPhoneValid) {
      setLocalError(getGrooverText('validationPhoneInvalid', selectedLanguage))
      return
    }

    if (!isEmailValid) {
      setLocalError(getGrooverText('validationEmailInvalid', selectedLanguage))
      return
    }

    setIsSubmitting(true)
    setLocalError(undefined)

    try {
      const internationalPhone = getInternationalPhoneNumber(phoneNumber, selectedPhoneCountry as any)
      await onSubmit(internationalPhone, email.trim())
    } catch {
      setLocalError(getGrooverText('portalLoginEmailLinkError', selectedLanguage))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Title>
        <GrooverText textKey="portalLoginEmailStepTitle" />
      </Title>
      <Description>
        <GrooverText textKey="portalLoginEmailStepDescription" />
      </Description>

      <InputWrapper>
        <PhoneInputWithFlag
          name="phoneNumber"
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value)
            if (localError) setLocalError(undefined)
          }}
          onCountryChange={setSelectedPhoneCountry}
          placeholder="70 123 45 67"
          defaultCountry={DEFAULT_PHONE_COUNTRY}
          disabled={isSubmitting}
          fullWidth
        />
      </InputWrapper>

      <InputWrapper>
        <Label htmlFor="portal-email">
          <GrooverText textKey="portalLoginEmailLabel" />
        </Label>
        <EmailInput
          id="portal-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            if (localError) setLocalError(undefined)
          }}
          disabled={isSubmitting}
        />
      </InputWrapper>

      {displayError && <ErrorText>{displayError}</ErrorText>}

      <SubmitButton type="submit" disabled={isSubmitting || !isPhoneValid || !isEmailValid}>
        {isSubmitting ? (
          <GrooverText textKey="portalLoginEmailSending" />
        ) : (
          <GrooverText textKey="portalLoginEmailSendLink" />
        )}
      </SubmitButton>
    </Form>
  )
}
